import React, { useRef, useState } from 'react';
import { Loader2, Lock, CreditCard, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { useSquarePayments } from '../../hooks/useSquarePayments';
import { useSquareCard } from '../../hooks/useSquareCard';
import { taxCents, fmtUsd } from '../../lib/tax';

// Renders the Square Web Payments SDK card form and tokenizes on submit.
// Only the deposit is charged here; the balance is collected at the appointment.
export const StepPayment = ({ depositCents, totalCents, baseCents, addonLines = [], discountCents = 0, promoLabel, taxRate, defaultCardholderName, onTokenized, onBack, submitting, errorMessage }) => {
  const { payments, error: sdkError } = useSquarePayments();
  const cardContainerRef = useRef(null);
  const { card, attaching, error: cardError } = useSquareCard(payments, cardContainerRef);
  const [localError, setLocalError] = useState(null);
  const [cardholderName, setCardholderName] = useState(defaultCardholderName || '');

  const handlePay = async () => {
    if (!card) return;
    setLocalError(null);
    if (!cardholderName.trim()) {
      setLocalError('Please enter the cardholder name.');
      return;
    }
    try {
      const result = await card.tokenize();
      if (result.status === 'OK') {
        onTokenized(result.token, cardholderName.trim());
      } else {
        const errs = (result.errors || []).map((e) => e.message).join(' ');
        setLocalError(errs || 'Card could not be verified. Please check details and try again.');
      }
    } catch (e) {
      console.warn('[square] tokenize failed', e);
      setLocalError('Card processing failed. Please try again.');
    }
  };

  const fmtAmount = fmtUsd;
  const rateBp = taxRate?.rate_bp || 0;
  const totalTax = taxCents(totalCents, rateBp);
  const depositTax = taxCents(depositCents, rateBp);
  const depositDue = depositCents + depositTax;
  const balanceCents = Math.max(0, totalCents + totalTax - depositDue);
  const error = errorMessage || localError || cardError || sdkError;

  return (
    <div className="space-y-5">
      <div className="bg-neutral-50 border border-neutral-200 rounded-sm divide-y divide-neutral-200">
        {(addonLines.length > 0 || discountCents > 0) && (
          <>
            <div className="flex items-center justify-between p-4">
              <span className="text-neutral-600 text-sm">Service</span>
              <span className="text-neutral-900" data-testid="payment-base-price">{fmtAmount(baseCents)}</span>
            </div>
            {addonLines.map((a) => (
              <div key={a.modifier_id} className="flex items-center justify-between px-4 py-3" data-testid="payment-addon-line">
                <span className="text-neutral-600 text-sm">+ {a.group}{a.quantity > 1 ? ` × ${a.quantity}` : ''}</span>
                <span className="text-neutral-900">{fmtAmount(a.total_cents)}</span>
              </div>
            ))}
            {discountCents > 0 && (
              <div className="flex items-center justify-between px-4 py-3" data-testid="payment-discount-line">
                <span className="text-green-700 text-sm font-semibold">{promoLabel || 'Discount'}</span>
                <span className="text-green-700 font-semibold">-{fmtAmount(discountCents)}</span>
              </div>
            )}
          </>
        )}
        <div className="flex items-center justify-between p-4">
          <span className="text-neutral-600 text-sm">Service Total</span>
          <span className="text-neutral-900" data-testid="payment-service-total">{fmtAmount(totalCents)}</span>
        </div>
        <div className="flex items-center justify-between p-4">
          <span className="text-neutral-600 text-sm">Tax ({taxRate?.city} {taxRate?.rate_percent}%)</span>
          <span className="text-neutral-900" data-testid="payment-tax">{fmtAmount(totalTax)}</span>
        </div>
        <div className="flex items-center justify-between p-4">
          <span className="text-neutral-900 text-sm font-semibold">Total incl. Tax</span>
          <span className="text-neutral-900 font-semibold" data-testid="payment-total-with-tax">{fmtAmount(totalCents + totalTax)}</span>
        </div>
        <div className="flex items-center justify-between p-4">
          <div>
            <div className="text-neutral-900 font-semibold">Deposit Due Now</div>
            <div className="text-neutral-500 text-xs mt-0.5" data-testid="payment-deposit-breakdown">{fmtAmount(depositCents)} deposit + {fmtAmount(depositTax)} tax, applied to your total</div>
          </div>
          <span className="text-2xl font-bold text-red-600" data-testid="payment-deposit">{fmtAmount(depositDue)}</span>
        </div>
        <div className="flex items-center justify-between p-4">
          <div>
            <div className="text-neutral-700 text-sm">Balance Due at Appointment</div>
            <div className="text-neutral-500 text-xs mt-0.5">Includes tax, paid directly to Stefen on arrival</div>
          </div>
          <span className="text-neutral-900" data-testid="payment-balance">{fmtAmount(balanceCents)}</span>
        </div>
      </div>

      <div className="rounded-md border-2 border-red-600/40 bg-white shadow-[0_8px_32px_rgba(220,38,38,0.15)] overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 bg-neutral-100 border-b border-neutral-200 px-4 sm:px-5 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-neutral-900 font-bold text-sm leading-tight">Payment Method</div>
              <div className="text-neutral-500 text-xs leading-tight">Enter your card details below</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-neutral-500 text-xs font-medium">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
            <span>Secured by Square</span>
          </div>
        </div>

        <div className="p-5 bg-white space-y-4">
          <div>
            <label htmlFor="cardholder-name" className="block text-neutral-700 text-xs font-bold uppercase tracking-wider mb-1.5">
              Cardholder Name
            </label>
            <input
              id="cardholder-name"
              type="text"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="Name on card"
              autoComplete="cc-name"
              maxLength={160}
              className="w-full px-3 py-2.5 text-base text-neutral-900 bg-white border border-neutral-300 rounded-md focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
              data-testid="input-cardholder-name"
            />
          </div>

          <div>
            <label className="block text-neutral-700 text-xs font-bold uppercase tracking-wider mb-1.5">
              Card Details
            </label>
            {attaching && (
              <div className="flex items-center text-neutral-500 text-sm py-2">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading secure card form…
              </div>
            )}
            <div ref={cardContainerRef} data-testid="square-card-container" />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 bg-neutral-50 border-t border-neutral-200 px-4 sm:px-5 py-2.5">
          <div className="flex items-center gap-1.5 text-neutral-500 text-xs">
            <Lock className="w-3 h-3" />
            <span>256-bit encrypted</span>
          </div>
          <div className="flex items-center gap-2 text-neutral-400 text-[10px] font-bold tracking-wider">
            <span>VISA</span>
            <span>•</span>
            <span>MASTERCARD</span>
            <span>•</span>
            <span>AMEX</span>
            <span>•</span>
            <span>DISCOVER</span>
          </div>
        </div>
      </div>

      <p className="text-neutral-500 text-xs text-center">
        Your card details are encrypted and processed directly by Square. They never touch our servers.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-red-700 text-sm" data-testid="payment-error">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onBack} disabled={submitting} className="border-neutral-300 text-neutral-900 bg-white hover:bg-neutral-100">
          Back
        </Button>
        <Button
          type="button"
          onClick={handlePay}
          disabled={!card || submitting}
          className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-neutral-200 disabled:text-neutral-500"
          data-testid="pay-and-book-btn"
        >
          {submitting ? (
            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Processing…</span>
          ) : (
            `Pay ${fmtAmount(depositDue)} Deposit & Book`
          )}
        </Button>
      </div>
    </div>
  );
};
